// Roster and attendance (docs/plans/phase3-operations-modules.md §4): shift times in East
// Africa Time as UTC instants, and attendance matched against the roster. Pure: callers
// pass the time. No payroll (Phase 4).

import { err, ok, type Result } from '../result.ts';
import { parseIsoDay, startOfEatDay } from '../time.ts';

export const ROSTER_UNIT_TYPES = ['ward', 'department'] as const;
export type RosterUnitType = (typeof ROSTER_UNIT_TYPES)[number];

export const ROSTER_ENTRY_STATUSES = ['active', 'removed'] as const;
export const ROSTER_SWAP_STATUSES = ['requested', 'approved', 'rejected', 'cancelled'] as const;
export type RosterSwapStatus = (typeof ROSTER_SWAP_STATUSES)[number];

export const ROSTER_SWAP_STATUS_LABELS: Record<RosterSwapStatus, string> = {
  requested: 'Waiting for approval',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

export const ATTENDANCE_STATES = [
  'present',
  'late',
  'left_early',
  'late_and_left_early',
  'absent',
  'not_yet',
] as const;
export type AttendanceState = (typeof ATTENDANCE_STATES)[number];

export const ATTENDANCE_STATE_LABELS: Record<AttendanceState, string> = {
  present: 'Present',
  late: 'Late',
  left_early: 'Left early',
  late_and_left_early: 'Late and left early',
  absent: 'Absent',
  not_yet: 'Not started yet',
};

/** Minutes either side of a shift before arriving counts as late or leaving as early (plan R3). */
export const ATTENDANCE_GRACE_MINUTES = 15;

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

export type RosterErrorCode =
  | 'roster.time_invalid'
  | 'roster.swap_own_approval'
  | 'roster.already_clocked_in'
  | 'roster.not_clocked_in';

export const ROSTER_ERROR_MESSAGES: Record<RosterErrorCode, string> = {
  'roster.time_invalid': 'Write times on the 24-hour clock, like 07:00 or 19:30.',
  'roster.swap_own_approval': 'Someone else approves your swap request.',
  'roster.already_clocked_in': "You're already clocked in. Clock out first.",
  'roster.not_clocked_in': "You aren't clocked in.",
};

export function checkClockTime(text: string): Result<string, RosterErrorCode> {
  return TIME_PATTERN.test(text.trim()) ? ok(text.trim()) : err('roster.time_invalid');
}

function minutesOf(time: string): number {
  const match = TIME_PATTERN.exec(time);
  if (match === null) throw new RangeError('not a clock time');
  return Number(match[1]) * 60 + Number(match[2]);
}

/**
 * The shift on an EAT day as UTC instants. An end not after the start is on the next day
 * (a night shift).
 */
export function shiftWindow(onDate: string, startsAt: string, endsAt: string) {
  const day = parseIsoDay(onDate);
  if (!day.ok) throw new RangeError('not a day');
  const midnight = startOfEatDay(day.value).getTime();
  const start = minutesOf(startsAt);
  let end = minutesOf(endsAt);
  if (end <= start) end += 24 * 60;
  return { start: new Date(midnight + start * 60_000), end: new Date(midnight + end * 60_000) };
}

export interface RosterSlot {
  entryId: string;
  userId: string;
  onDate: string;
  startsAt: string;
  endsAt: string;
}

export interface ClockRecord {
  id: string;
  userId: string;
  clockInAt: Date;
  clockOutAt: Date | null;
}

/**
 * Each roster entry with its state, and the clock records that match no entry. A clock
 * record matches an entry of the same person when it overlaps the shift; each record matches
 * at most one entry.
 */
export function attendanceAgainstRoster(
  slots: readonly RosterSlot[],
  clocks: readonly ClockRecord[],
  now: Date,
  graceMinutes: number = ATTENDANCE_GRACE_MINUTES,
): {
  entries: { entryId: string; state: AttendanceState; clockId: string | null }[];
  unrostered: ClockRecord[];
} {
  const grace = graceMinutes * 60_000;
  const used = new Set<string>();
  const entries = slots.map((slot) => {
    const { start, end } = shiftWindow(slot.onDate, slot.startsAt, slot.endsAt);
    const clock = clocks.find(
      (c) =>
        !used.has(c.id) &&
        c.userId === slot.userId &&
        c.clockInAt.getTime() < end.getTime() &&
        (c.clockOutAt ?? now).getTime() > start.getTime() - grace,
    );
    if (clock === undefined) {
      return {
        entryId: slot.entryId,
        state: (now.getTime() < end.getTime() ? 'not_yet' : 'absent') as AttendanceState,
        clockId: null,
      };
    }
    used.add(clock.id);
    const late = clock.clockInAt.getTime() > start.getTime() + grace;
    const early = clock.clockOutAt !== null && clock.clockOutAt.getTime() < end.getTime() - grace;
    const state: AttendanceState =
      late && early ? 'late_and_left_early' : late ? 'late' : early ? 'left_early' : 'present';
    return { entryId: slot.entryId, state, clockId: clock.id };
  });
  return { entries, unrostered: clocks.filter((c) => !used.has(c.id)) };
}
